import { Button } from "@/components/flexkit/Button";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useCreateRunnerAccessToken } from "@/queries/runner-queries";
import { createRunnerSetupText } from "@/components/runners/RunnerSetupURL";
import { formatError } from "@/utils/errors";
import type { PlainMessage } from "@bufbuild/protobuf";
import { type Runner } from "gitpod-next-api/gitpod/v1/runner_pb";
import { useCallback, type FC } from "react";
import IconSquareBehindSquare2 from "central-icons/IconSquareBehindSquare2";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import IconCheckmark1 from "central-icons/IconCheckmark1";

const CopyIcon: FC = () => <IconSquareBehindSquare2 size={16} />;
const CheckmarkIcon: FC = () => <IconCheckmark1 size={16} className="text-content-success" />;

export const CloudFormationStackCopyDetails: FC<{ runner?: PlainMessage<Runner>; onClicked?: () => void }> = ({
    runner,
    onClicked,
}) => {
    const { toast } = useToast();
    const createRunnerToken = useCreateRunnerAccessToken();

    const [copied, setCopied] = useTemporaryState(false, 2000);

    const onCopyDetails = useCallback(async () => {
        if (!runner) {
            return;
        }
        if (onClicked) {
            onClicked();
        }
        setCopied(true);
        try {
            const region = runner.spec?.configuration?.region;
            const { accessToken } = await createRunnerToken.mutateAsync(runner.runnerId);
            // Only enterprise template is supported in the dashboard
            await navigator.clipboard.writeText(
                createRunnerSetupText(runner, accessToken, region || "", undefined, "enterprise"),
            );
        } catch (error) {
            toast({
                title: "Failed to copy details to your clipboard",
                description: formatError(error),
            });
        }
    }, [runner, setCopied, createRunnerToken, toast, onClicked]);

    return (
        <div className="flex flex-wrap gap-1">
            <Button
                variant="outline"
                onClick={onCopyDetails}
                size="sm"
                LeadingIcon={copied ? CheckmarkIcon : CopyIcon}
                disabled={createRunnerToken.isPending}
                data-tracking-id="copy-cloudformation-stack-details"
            >
                <span className="text-sm">Copy Setup Details</span>
            </Button>
        </div>
    );
};
